import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InboxIcon,
} from '@heroicons/react/24/outline';

import { PageHeader } from '@/components/layout';
import { Button, Card, Modal, ModalFooter } from '@/components/common';
import { getErrorMessage } from '@/services';
import { brokerMappingRequestsApi, type BrokerMappingRequest } from '@/services/pospPortal';
import { useAuthStore } from '@/store';
import { formatDate } from '@/utils';

/**
 * MAPQ / M-02 — "Register as POSP": the RECEIVING brokerage's half of the
 * broker-mapping-request flow.
 *
 * ## Where it lives (M-02, owner decision)
 *
 * These requests belong to Broker Admin alone, under Channels: the queue is
 * the "Register as POSP" tab of `ChannelsListPage` (`?tab=register-posp`),
 * rendered by `RegisterAsPospPanel` below. Country Admin used to reach a copy
 * from All Brokers; that entry point is gone and the API now refuses a country
 * admin too. The old `/brokers/mapping-requests` URL still works: a broker
 * admin is forwarded to the Channels tab, and a super admin (ops support, the
 * only other role the API admits) gets this standalone page.
 *
 * The agent's side (`PospBrokerMappingCard`, `pages/settings/`) already lets an
 * agent ask to be mapped in and see the answer. Until this page, nothing let
 * the broker on the other end of that ask see it, short of calling
 * `POST .../approve/` by hand — a request filed and never actioned is
 * indistinguishable, from the agent's chair, from one nobody read.
 *
 * ## Why approving needs a modal, not a button
 *
 * `POSP.broker` is the tenant key for the agent's entire book. Approving can:
 *   - re-point `POSP.broker` to this brokerage,
 *   - reissue `posp_code` if the agent's existing code collides here,
 *   - clear `parent_posp` (a sub-agent link that only made sense at the old
 *     brokerage),
 *   - drop `status` back to `pending` (activating them is this brokerage's own
 *     decision, not something the approval grants for free),
 *   - re-point every `POSPRight` row the agent holds.
 * None of that is reversible from this screen, so the confirm step spells it
 * out before the click that does it — see `CONSEQUENCES_TEXT` below, which
 * mirrors `BrokerMappingRequestViewSet._apply_transfer`'s own docstring.
 *
 * ## Why an approval can still say "not moved"
 *
 * An agent who already has policies, quotes, customers, or other attached
 * business is NOT re-pointed automatically (`transfer_blockers()` on the
 * backend) — the approval is recorded as this brokerage's consent, but
 * `applied` stays `false` and `blocked_reason` names exactly what is in the
 * way. Rendering "Approved" alone here would tell this brokerage's admin the
 * agent is now theirs when nothing moved, so every render of an approved row
 * -- and the outcome banner right after acting on one -- reads `applied`
 * before choosing its words.
 *
 * ## Why the outcome banner re-reads the list instead of trusting the POST
 *
 * This platform's recurring bug is an action that reports success and
 * persists nothing. The approve/decline actions already `refresh_from_db()`
 * before responding, so the POST response is not naive here -- but per the
 * house rule, a write path is verified by what a subsequent read reports, not
 * by what the write handler said about itself. `confirmDecision` below makes
 * an independent, uncached `GET .../?status=all` after every verdict and reads
 * ITS copy of the row for the banner text; if that re-read cannot find the row
 * in the expected state, the banner says so plainly instead of asserting a
 * result nobody has confirmed.
 */

const CONSEQUENCES_TEXT =
  "Approving points this agent's account at your brokerage: their agent code may be reissued " +
  'if it collides with one already in use here, any parent-agent link they had is cleared, their ' +
  'status resets to pending until you activate them, and their module rights are re-pointed to ' +
  'your brokerage. If they already have policies, quotes, customers or other business attached, ' +
  'none of that moves automatically — approving only records your consent, and this screen will ' +
  'show you exactly what stopped it.';

type Decision = 'approve' | 'decline';

interface DecideTarget {
  row: BrokerMappingRequest;
  decision: Decision;
}

/** What the admin is told after a verdict, built from a fresh re-read of the
 *  list rather than the mutation's own response. `verified: false` means the
 *  re-read could not confirm the row landed where the POST said it would --
 *  distinct from, and never worded like, a confirmed outcome. */
interface Outcome {
  decision: Decision;
  pospName: string;
  toBrokerName: string;
  verified: boolean;
  applied?: boolean;
  blockedReason?: string;
  declineReason?: string;
}

export const REGISTER_AS_POSP_TAB = 'register-posp';
const PENDING_KEY = ['posp-broker-mapping-requests', 'pending'];

/** The pending queue. One query key, so the standalone page's header and the
 *  panel share a single request. */
function usePendingRegisterAsPosp() {
  return useQuery({
    queryKey: PENDING_KEY,
    queryFn: () => brokerMappingRequestsApi.list({ status: 'pending' }),
  });
}

/** The queue itself -- outcome banner, table and both verdict modals. Mounted
 *  as a tab inside Channels and by the standalone page. */
export function RegisterAsPospPanel({ showToolbar = false }: { showToolbar?: boolean }) {
  const queryClient = useQueryClient();
  const [decideTarget, setDecideTarget] = useState<DecideTarget | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [modalError, setModalError] = useState('');
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const pendingKey = PENDING_KEY;
  const pendingQuery = usePendingRegisterAsPosp();

  const rows = pendingQuery.data?.results ?? [];

  const verdictMutation = useMutation({
    mutationFn: ({ row, decision }: DecideTarget) =>
      decision === 'approve'
        ? brokerMappingRequestsApi.approve(row.id)
        : brokerMappingRequestsApi.decline(row.id, declineReason.trim()),
  });

  function closeModal() {
    setDecideTarget(null);
    setDeclineReason('');
    setModalError('');
  }

  async function confirmDecision() {
    if (!decideTarget) return;
    const { row, decision } = decideTarget;
    setModalError('');
    try {
      await verdictMutation.mutateAsync(decideTarget);

      // Never trust the POST response for what we tell the admin. An
      // independent, uncached read of the list is what actually answers
      // "did this persist" -- `status: 'all'` because an approved/declined
      // row has already left the 'pending' filter by the time we ask.
      const fresh = await brokerMappingRequestsApi.list({ status: 'all' });
      const confirmedRow = fresh.results.find((candidate) => candidate.id === row.id);
      const expectedStatus = decision === 'approve' ? 'approved' : 'declined';
      const verified = Boolean(confirmedRow && confirmedRow.status === expectedStatus);

      setOutcome({
        decision,
        pospName: row.posp_name || row.posp_code,
        toBrokerName: row.to_broker_name,
        verified,
        applied: confirmedRow?.applied,
        blockedReason: confirmedRow?.blocked_reason,
        declineReason: confirmedRow?.decline_reason,
      });
      closeModal();
      // The pending list is the thing the admin acts against next; it must
      // reflect the decision immediately, not on the next unrelated refetch.
      await queryClient.invalidateQueries({ queryKey: pendingKey });
    } catch (err) {
      setModalError(getErrorMessage(err));
    }
  }

  function renderOutcomeBanner() {
    if (!outcome) return null;

    if (!outcome.verified) {
      return (
        <div
          role="alert"
          className="mb-6 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-4 py-3"
        >
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
            The decision for {outcome.pospName} was sent, but re-checking the list just now did not
            show it as {outcome.decision === 'approve' ? 'approved' : 'declined'}.
          </p>
          <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
            Reload this page and confirm before assuming it went through.
          </p>
        </div>
      );
    }

    if (outcome.decision === 'decline') {
      return (
        <div className="mb-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
          <p className="text-sm text-gray-700 dark:text-gray-200">
            Declined {outcome.pospName}&rsquo;s request.{' '}
            {outcome.declineReason ? (
              <>The reason sent to them: &ldquo;{outcome.declineReason}&rdquo;</>
            ) : (
              'No reason reached their record — this should not happen; check the request.'
            )}
          </p>
        </div>
      );
    }

    // Approved. `applied` is read from the RE-READ row, not the POST
    // response, so this is what the database actually holds right now.
    if (outcome.applied) {
      return (
        <div className="mb-6 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 px-4 py-3">
          <p className="text-sm font-medium text-green-800 dark:text-green-300">
            {outcome.pospName} is now mapped to {outcome.toBrokerName}. They stay pending until you
            activate them.
          </p>
        </div>
      );
    }

    return (
      <div className="mb-6 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3">
        <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
          Approved, but {outcome.pospName} was NOT moved to {outcome.toBrokerName}.
        </p>
        <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
          {outcome.blockedReason ||
            'The API did not send a reason — check the request directly before treating this as safe to ignore.'}
        </p>
      </div>
    );
  }

  function renderBody() {
    if (pendingQuery.isLoading) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      );
    }

    if (pendingQuery.isError) {
      return (
        <div role="alert" className="px-6 py-16 text-center">
          <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-400" />
          <h3 className="mt-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
            Could not load Register as POSP requests
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            {getErrorMessage(pendingQuery.error)}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => pendingQuery.refetch()}
          >
            Try again
          </Button>
        </div>
      );
    }

    if (rows.length === 0) {
      return (
        <div className="px-6 py-16 text-center">
          <InboxIcon className="mx-auto h-10 w-10 text-gray-400" />
          <h3 className="mt-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
            Nothing is waiting
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            No agent has asked to register as a POSP with a brokerage you administer.
          </p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                Agent
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                From
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                To
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                Requested
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                Verdict
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                <td className="px-6 py-4">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {row.posp_name || 'Unnamed agent'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{row.posp_code}</p>
                  {row.note && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 max-w-md">
                      &ldquo;{row.note}&rdquo;
                    </p>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {row.from_broker_name || '—'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                  {row.to_broker_name}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(row.created_at)}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="inline-flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => setDecideTarget({ row, decision: 'approve' })}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setDecideTarget({ row, decision: 'decline' })}
                    >
                      Decline
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div>
      {showToolbar && (
        <div className="mb-4 flex items-center justify-between gap-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Agents asking to register as a POSP with your brokerage.{' '}
            {pendingQuery.isSuccess &&
              `${rows.length} pending request${rows.length !== 1 ? 's' : ''}.`}
          </p>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<ArrowPathIcon className="w-4 h-4" />}
            onClick={() => pendingQuery.refetch()}
          >
            Refresh
          </Button>
        </div>
      )}

      {renderOutcomeBanner()}

      <Card className="p-0 overflow-hidden">{renderBody()}</Card>

      {/* Approve: the consequences are spelled out every time, because the
          screen has no way to know in advance whether THIS agent will hit a
          transfer blocker -- that only comes back on the response. */}
      <Modal
        isOpen={decideTarget?.decision === 'approve'}
        onClose={closeModal}
        title="Approve this Register as POSP request?"
      >
        {decideTarget && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700 dark:text-gray-200">
              <span className="font-medium">{decideTarget.row.posp_name || decideTarget.row.posp_code}</span>{' '}
              asked to register as a POSP with <span className="font-medium">{decideTarget.row.to_broker_name}</span>.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{CONSEQUENCES_TEXT}</p>
            {modalError && <p className="text-sm text-red-600 dark:text-red-400">{modalError}</p>}
          </div>
        )}
        <ModalFooter>
          <Button variant="outline" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            isLoading={verdictMutation.isPending}
            onClick={() => confirmDecision()}
          >
            Approve
          </Button>
        </ModalFooter>
      </Modal>

      {/* Decline: the reason is mandatory here on the frontend -- the API
          itself will happily store an empty string, but a decline with no
          reason leaves the agent guessing why, which is the thing item 3 of
          this lane exists to close. */}
      <Modal
        isOpen={decideTarget?.decision === 'decline'}
        onClose={closeModal}
        title="Decline this Register as POSP request?"
      >
        {decideTarget && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {decideTarget.row.posp_name || decideTarget.row.posp_code} will see this reason on
              their own request.
            </p>
            <div>
              <label
                htmlFor="decline-reason"
                className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
              >
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                id="decline-reason"
                name="decline_reason"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-gray-100"
                rows={3}
                maxLength={2000}
                value={declineReason}
                onChange={(event) => setDeclineReason(event.target.value)}
                placeholder="Why this brokerage is declining — the agent sees this."
              />
            </div>
            {modalError && <p className="text-sm text-red-600 dark:text-red-400">{modalError}</p>}
          </div>
        )}
        <ModalFooter>
          <Button variant="outline" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            isLoading={verdictMutation.isPending}
            disabled={!declineReason.trim()}
            onClick={() => confirmDecision()}
          >
            Decline Request
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

/** `/brokers/mapping-requests` -- the old URL. A broker admin is forwarded to
 *  the Channels tab, which is where the queue lives now; a super admin (ops
 *  support; Channels is broker-admin only) gets the queue on its own page. */
export function PospMappingRequestsPage() {
  const role = useAuthStore((state) => state.user?.role);
  const pendingQuery = usePendingRegisterAsPosp();
  if (role === 'broker_admin') {
    return <Navigate to={`/broker/channels?tab=${REGISTER_AS_POSP_TAB}`} replace />;
  }
  const count = pendingQuery.data?.results.length ?? 0;
  return (
    <div>
      <PageHeader
        title="Register as POSP"
        subtitle={`${count} pending request${count !== 1 ? 's' : ''}`}
        breadcrumbs={[{ label: 'Register as POSP' }]}
        actions={
          <Button
            variant="outline"
            leftIcon={<ArrowPathIcon className="w-5 h-5" />}
            onClick={() => pendingQuery.refetch()}
          >
            Refresh
          </Button>
        }
      />
      <RegisterAsPospPanel />
    </div>
  );
}

export default PospMappingRequestsPage;
